/**
 * Gift Auto Add
 *
 * カート内商品のタグに基づき、特典商品を自動的にカートに追加・更新する。
 * 付与の判定ロジックは Shopify Flow 側の実装と揃えている。
 * Flow は OrderCreate をトリガーとし、フロント側で付与しきれなかった分を
 * 注文作成時に補完（バリデーション）する役割を持つ。
 *
 * 【動作概要】
 * 1. ページロード時にカートを確認してギフトルールを適用する
 * 2. fetch をインターセプトし、カートへの商品追加・数量変更後にギフトルールを再適用する
 * 3. ギフト商品の付与数量 = giftQuantity × 対象タグを持つラインアイテム数量の合計
 * 4. 対象タグを持つアイテムがない場合はギフト商品を削除する
 * 5. 開始・終了日時の期間外のルールは適用しない（Flow の isRuleActive と同じ判定）。
 *    期間外ルールの特典がカートに残っている場合は削除する
 *
 * 【依存】
 * - window.umgGiftRules: theme.liquid でセットされるギフトルール配列
 *   [{ operationName, giftQuantity, startAt: number|null, endAt: number|null,
 *      tagNames: string[], giftVariantIds: number[] }, ...]
 *   startAt / endAt は UNIX 秒。null は「指定なし」
 * - /cart/update.js: カート更新 JSON API（window.routes.cart_update_url の /cart/update はHTML用のため不可）
 * - window.refreshSections: section-render.bundle.js でセットされるセクション更新関数
 */
(function () {
  'use strict';

  // ギフトルールが設定されていない場合はスキップ
  if (!window.umgGiftRules || !window.umgGiftRules.length) return;

  /** 再帰的な fetch 呼び出しを防ぐフラグ */
  let isApplyingGifts = false;

  /**
   * applyGiftRules 実行中にカート変更が発生した場合に再実行を予約するフラグ。
   * 競合状態（isApplyingGifts = true のときに /cart/add や /cart/change が来る）を解消する。
   */
  let pendingGiftApply = false;

  /** オリジナルの fetch を保存 */
  const originalFetch = window.fetch.bind(window);

  /**
   * 商品ハンドル → タグ配列のキャッシュ
   * /cart.js はカートアイテムの tags を返さないため、
   * /products/{handle}.js を叩いてタグを取得しキャッシュする。
   */
  const productTagsCache = {};

  /**
   * サーバー時刻とクライアント時刻の差（ミリ秒）。
   * お客様の PC の時計がずれていても期間判定がずれないよう、
   * カート API のレスポンスの Date ヘッダーから補正する。
   */
  let serverClockOffsetMs = 0;

  /**
   * レスポンスの Date ヘッダーからサーバー時刻との差を更新する。
   * Date ヘッダーが無い・読めない場合はクライアント時刻のまま。
   * @param {Response} response
   */
  function syncServerClock(response) {
    const dateHeader = response && response.headers && response.headers.get('Date');
    const serverMs = dateHeader ? Date.parse(dateHeader) : NaN;
    if (!Number.isNaN(serverMs)) {
      serverClockOffsetMs = serverMs - Date.now();
    }
  }

  /**
   * 現在時刻（UNIX 秒、サーバー時刻で補正済み）
   * @returns {number}
   */
  function nowSeconds() {
    return Math.floor((Date.now() + serverClockOffsetMs) / 1000);
  }

  /**
   * ルールが現在時刻で有効期間内かを判定する（Flow の isRuleActive と同じ）。
   * 開始・終了ともに境界を含む。どちらも未指定なら常に有効。
   * @param {{ startAt?: number|null, endAt?: number|null }} rule
   * @param {number} now UNIX 秒
   * @returns {boolean}
   */
  function isRuleActive(rule, now) {
    const start = typeof rule.startAt === 'number' ? rule.startAt : null;
    const end = typeof rule.endAt === 'number' ? rule.endAt : null;
    if (start !== null && now < start) return false;
    if (end !== null && now > end) return false;
    return true;
  }

  /**
   * 現在のユーザーが日本対象かどうかを判定する。
   * IP ベースの国（localization.country）またはログイン中の顧客のデフォルト住所の国が
   * JP であれば true を返す。
   * window.umgGiftJpOnly が未設定の場合は制限しない（true を返す）。
   * @returns {boolean}
   */
  function isJapanUser() {
    const jpOnly = window.umgGiftJpOnly;
    if (!jpOnly) return true;
    return jpOnly.geoCountryCode === 'JP' || jpOnly.customerCountryCode === 'JP';
  }

  /**
   * 商品ハンドルからタグ一覧を取得する（キャッシュ付き）
   * @param {string} handle
   * @returns {Promise<string[]>}
   */
  async function fetchProductTags(handle) {
    if (productTagsCache[handle] !== undefined) {
      return productTagsCache[handle];
    }
    try {
      const res = await originalFetch(`/products/${handle}.js`);
      if (!res.ok) {
        productTagsCache[handle] = [];
        return [];
      }
      const data = await res.json();
      // /products/{handle}.js の tags はカンマ区切り文字列
      const tags = parseItemTags(data.tags || '');
      productTagsCache[handle] = tags;
      return tags;
    } catch (_) {
      productTagsCache[handle] = [];
      return [];
    }
  }

  /**
   * カートアイテムに tags フィールドをエンリッチする。
   * ギフト商品以外のアイテムの商品タグを /products/{handle}.js から並行取得する。
   * @param {Object[]} cartItems
   * @param {Set<number>} allGiftVariantIds
   */
  async function enrichCartItemsWithTags(cartItems, allGiftVariantIds) {
    const nonGiftItems = cartItems.filter(
      (item) => !allGiftVariantIds.has(item.variant_id) && item.handle
    );
    await Promise.all(
      nonGiftItems.map(async (item) => {
        item.tags = await fetchProductTags(item.handle);
      })
    );
  }

  /**
   * カートアイテムのタグ文字列（カンマ区切り）を配列に変換する
   * @param {string|string[]} tags
   * @returns {string[]}
   */
  function parseItemTags(tags) {
    if (!tags) return [];
    if (Array.isArray(tags)) return tags.map((t) => t.trim());
    if (typeof tags === 'string') {
      return tags.split(',').map((t) => t.trim()).filter(Boolean);
    }
    return [];
  }

  /**
   * 現在のカートアイテムとギフトルールから、
   * カートに反映すべきギフト商品の updates オブジェクトを計算する。
   *
   * @param {Object[]} cartItems  /cart.js の items 配列
   * @returns {{ updates: Object, needsUpdate: boolean }}
   */
  function computeGiftUpdates(cartItems) {
    // 全ルールにわたるギフトバリアント ID セットを構築。
    // 期間外のルールの特典も含めることで、カートに残っている期間外の特典を削除対象にする
    const allGiftVariantIds = new Set(
      window.umgGiftRules.flatMap((rule) => rule.giftVariantIds)
    );

    // 付与の判定に使うのは有効期間内のルールだけ
    const now = nowSeconds();
    const giftRules = window.umgGiftRules.filter((rule) => isRuleActive(rule, now));

    // ギフト以外のアイテム（タグ判定の対象）
    const nonGiftItems = cartItems.filter(
      (item) => !allGiftVariantIds.has(item.variant_id)
    );

    // 各ギフトバリアントの希望数量を計算
    const desiredQuantities = {};

    for (const rule of giftRules) {
      const tagNames = Array.isArray(rule.tagNames)
        ? rule.tagNames
        : [rule.tagNames].filter(Boolean);

      // 対象タグを持つアイテムを抽出
      const targetItems = nonGiftItems.filter((item) => {
        const itemTags = parseItemTags(item.tags);
        return tagNames.some((tag) => itemTags.includes(tag));
      });

      // 対象アイテムがなければこのルールはスキップ
      if (targetItems.length === 0) continue;

      // 付与数量 = giftQuantity × 対象アイテムの合計数量
      const newQuantity = targetItems.reduce(
        (sum, item) => sum + rule.giftQuantity * item.quantity,
        0
      );

      // 各ギフトバリアントに数量を累積（複数ルールで同じバリアントが対象になる場合）
      for (const variantId of rule.giftVariantIds) {
        desiredQuantities[variantId] =
          (desiredQuantities[variantId] || 0) + newQuantity;
      }
    }

    // 現在のカートと希望数量を比較して updates を構築
    const updates = {};
    let needsUpdate = false;

    // ギフトバリアントの希望数量を設定
    for (const [variantId, desiredQty] of Object.entries(desiredQuantities)) {
      const variantIdInt = parseInt(variantId, 10);
      const currentItem = cartItems.find(
        (item) => item.variant_id === variantIdInt
      );
      const currentQty = currentItem ? currentItem.quantity : 0;
      if (currentQty !== desiredQty) {
        updates[variantId] = desiredQty;
        needsUpdate = true;
      }
    }

    // 不要になったギフトアイテムをゼロに設定（対象タグがなくなった場合）
    for (const item of cartItems) {
      if (
        allGiftVariantIds.has(item.variant_id) &&
        desiredQuantities[item.variant_id] === undefined
      ) {
        updates[item.variant_id] = 0;
        needsUpdate = true;
      }
    }

    return { updates, needsUpdate };
  }

  /**
   * セクション HTML を対象要素に描画する。
   *
   * ヘッダーは PC 用（#header-cart-count）と MD 用（#header-cart-count_md）が
   * 同時に DOM に存在するため、両方を更新する。
   *
   * @param {{ id: string, dynamic: boolean }} section
   * @param {string} html セクションレンダリング API が返した HTML
   */
  function renderSection(section, html) {
    if (typeof html !== 'string') return;

    const sectionNode = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('.shopify-section');
    if (!sectionNode) return;

    const prefix = section.dynamic ? 'shopify-section-' : '';
    for (const id of [`${prefix}${section.id}`, `${prefix}${section.id}_md`]) {
      const target = document.getElementById(id);
      if (!target) continue;
      target.innerHTML = sectionNode.innerHTML;
      // cart-drawer は差し替えで閉じるボタンの参照が切れるため再バインドする
      if (section.id === 'cart-drawer' && typeof target.setupCloseButton === 'function') {
        target.setupCloseButton();
      }
    }
  }

  /**
   * カート UI セクションを再レンダリングする。
   * カートページとカートドロワーで更新するセクションが異なる。
   *
   * テーマ標準の window.refreshSections は #header-cart-count しか更新せず、
   * MD ヘッダーの #header-cart-count_md が古い件数のまま残る
   * （カートを空にしてもバッジが消えず、リロードで解消する）ため、
   * セクション取得と描画を自前で行う。
   */
  async function refreshCartUI() {
    // カートページ（/cart）の場合
    const cartElement = document.getElementById('cart');
    const sections =
      cartElement && cartElement.dataset.id
        ? [
            { id: 'header-cart-count', dynamic: false },
            { id: cartElement.dataset.id, dynamic: true },
          ]
        : // カートドロワーの場合
          [
            { id: 'cart-drawer', dynamic: false },
            { id: 'header-cart-count', dynamic: false },
          ];

    try {
      const sectionIds = sections.map((section) => section.id).join(',');
      const response = await originalFetch(`?sections=${sectionIds}`);
      if (!response.ok) throw new Error('sections request failed');
      const rendered = await response.json();
      for (const section of sections) {
        renderSection(section, rendered[section.id]);
      }
    } catch (_) {
      // 取得・描画に失敗した場合はテーマ標準の更新にフォールバックする
      if (typeof window.refreshSections === 'function') {
        window.refreshSections(sections);
      }
    }
  }

  /**
   * カート操作中のローディング状態を開始する。
   * body に is-gift-applying クラスを付与する。
   * CSS セレクタで制御するため、セクション再レンダリングで DOM が差し替わっても
   * 新しい要素に自動的に適用される。
   */
  function startLoadingState() {
    document.body.classList.add('is-gift-applying');
  }

  /**
   * ローディング状態を終了する。
   */
  function endLoadingState() {
    document.body.classList.remove('is-gift-applying');
  }

  /**
   * ギフトルールをカートに適用するメイン処理。
   * isApplyingGifts フラグで再帰呼び出しを防ぐ。
   * @param {Object} [options]
   * @param {boolean} [options.showLoading=false] - ユーザー操作起因の場合 true にしてローディング状態を維持する
   * @param {Object|null} [options.cart=null] - 事前取得済みのカートデータ（/cart/change レスポンスの再利用）
   */
  async function applyGiftRules({ showLoading = false, cart = null } = {}) {
    // 日本からのアクセスまたは日本住所のお客様以外はギフトを付与しない
    if (!isJapanUser()) {
      if (showLoading) endLoadingState();
      return;
    }
    if (isApplyingGifts) return;
    isApplyingGifts = true;
    if (showLoading) startLoadingState();

    try {
      // カート状態を取得。
      // /cart/change の呼び出し元から cart が渡された場合はそれを再利用し、
      // /cart.js への余分なリクエストを省く（1往復分の短縮）。
      if (!cart) {
        const cartResponse = await originalFetch('/cart.js', {
          headers: { 'Content-Type': 'application/json' },
        });
        if (!cartResponse.ok) return;
        syncServerClock(cartResponse);
        cart = await cartResponse.json();
      }

      // /cart.js は items に tags を含まないため、商品ごとに取得してエンリッチ
      const allGiftVariantIds = new Set(
        window.umgGiftRules.flatMap((rule) => rule.giftVariantIds)
      );
      await enrichCartItemsWithTags(cart.items, allGiftVariantIds);

      if (window.umgGiftDebug) {
        console.group('[GiftAutoAdd] applyGiftRules');
        console.log('umgGiftRules:', JSON.stringify(window.umgGiftRules, null, 2));
        console.log('cart.items:', cart.items.map((i) => ({
          title: i.title, variant_id: i.variant_id, quantity: i.quantity, handle: i.handle, tags: i.tags
        })));
      }

      const { updates, needsUpdate } = computeGiftUpdates(cart.items);

      if (window.umgGiftDebug) {
        console.log('updates:', updates, '/ needsUpdate:', needsUpdate);
        console.groupEnd();
      }

      if (!needsUpdate) return;

      // カートを更新
      // /cart/update.js (JSON AJAX API) を使用。
      // window.routes.cart_update_url は /cart/update（HTML フォーム用）のため約3秒かかる。
      // .js エンドポイントは JSON を返し約1.4秒で完了する。
      const updateResponse = await originalFetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!updateResponse.ok) return;

      // 付与完了ログ
      console.log(
        '[GiftAutoAdd] 特典付与完了:',
        Object.entries(updates)
          .map(([id, qty]) => `variant_id=${id} → qty=${qty}`)
          .join(', ')
      );

      // カート UI を更新（描画完了までローディング状態を維持する）
      await refreshCartUI();
    } catch (_) {
      // ギフト機能はノンクリティカルのためエラーは握りつぶす
    } finally {
      isApplyingGifts = false;
      // 実行中にカート変更があった場合は再実行する（競合状態の解消）
      if (pendingGiftApply) {
        pendingGiftApply = false;
        // 次の applyGiftRules が endLoadingState を担当するため、ここでは呼ばない。
        // これにより「ローディング解除 → 即再開始」の一瞬のチラつきを防ぐ。
        setTimeout(() => applyGiftRules({ showLoading: true }), 0);
      } else {
        if (showLoading) endLoadingState();
      }
    }
  }

  /**
   * window.fetch をインターセプトしてカートへの変更を検知する。
   * カートへの商品追加・数量変更後にギフトルールを適用する。
   *
   * 注意: applyGiftRules 実行中（isApplyingGifts = true）に /cart/add や /cart/change が
   * 発生した場合、pendingGiftApply フラグを立てて完了後に再実行する。
   * これにより、ページロード直後の applyGiftRules 実行中に PDP からカートに追加した場合でも
   * 確実に特典が付与される（競合状態の解消）。
   */
  window.fetch = function (...args) {
    const promise = originalFetch.apply(window, args);

    const url = args[0];
    const urlStr =
      typeof url === 'string'
        ? url
        : url && typeof url.url === 'string'
        ? url.url
        : '';

    const isCartAdd = urlStr.includes('/cart/add');
    const isCartChange = urlStr.includes('/cart/change');

    if (isCartChange) {
      // /cart/change.js はフルカート JSON を返すため、response を clone して
      // applyGiftRules に渡すことで後続の GET /cart.js を省略できる。
      promise
        .then(async (response) => {
          if (response.ok) {
            syncServerClock(response);
            const cart = await response.clone().json().catch(() => null);
            if (isApplyingGifts) {
              pendingGiftApply = true;
              startLoadingState();
            } else {
              startLoadingState();
              setTimeout(() => applyGiftRules({ showLoading: true, cart }), 0);
            }
          }
        })
        .catch(() => {});
    }

    if (isCartAdd) {
      // /cart/add.js はフルカートではなく追加ラインアイテムを返すため、
      // cart は渡せず applyGiftRules 内で GET /cart.js を実行する。
      promise
        .then((response) => {
          if (response.ok) {
            if (isApplyingGifts) {
              pendingGiftApply = true;
              startLoadingState();
            } else {
              startLoadingState();
              setTimeout(() => applyGiftRules({ showLoading: true }), 0);
            }
          }
        })
        .catch(() => {});
    }

    return promise;
  };

  // ページロード時に既存カートを確認してギフトルールを適用
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () =>
      setTimeout(applyGiftRules, 500)
    );
  } else {
    setTimeout(applyGiftRules, 500);
  }
})();
